declare global {
  interface Window {
    wsConnection: WebSocket | null
  }
}

export const prefix = 'webSockets'

export const CREATE_ACTIVITY = `${prefix}/CREATE_ACTIVITY_SUCCESS`
export const UPDATE_ACTIVITY = `${prefix}/UPDATE_ACTIVITY_SUCCESS`
export const DELETE_ACTIVITY = `${prefix}/DELETE_ACTIVITY_SUCCESS`
export const UPDATE_LOAN_BALANCE = `${prefix}/UPDATE_LOAN_BALANCE_SUCCESS`
export const UPDATE_NOTIFICATIONS = `${prefix}/UPDATE_NOTIFICATIONS`
export const UPDATE_BBC_LOG = `${prefix}/UPDATE_BBC_LOG_SUCCESS`
export const UPDATE_FUNDING_REQUEST = `${prefix}/UPDATE_FUNDING_REQUEST_SUCCESS`
export const UPDATE_OPS_REPORTING = `${prefix}/UPDATE_OPS_REPORTING`
export const UPDATE_ANALYSIS_EXPORT = `${prefix}/UPDATE_ANALYSIS_EXPORT`
export const CODAT_PORTFOLIO_SYNC = `${prefix}/CODAT_PORTFOLIO_SYNC`
export const UPDATE_DUE_DILIGENCE = `${prefix}/UPDATE_DUE_DILIGENCE`
export const WS_SEND_MESSAGE = `${prefix}/WS_SEND_MESSAGE`
export const WS_PING = `${prefix}/WS_PING`
export const WS_PONG = `${prefix}/WS_PONG`
export const ATLAS_AI_THINKING = `${prefix}/ATLAS_AI_THINKING`
// Per-turn text emitted by the agent. Used for both live UX (show Arc's
// reasoning while it works) and diagnostic visibility (observe what each turn
// produced, since the tool loop only returns the final turn's text).
export const ATLAS_AI_TURN_TEXT = `${prefix}/ATLAS_AI_TURN_TEXT`
export const ATLAS_AI_COMPLETE = `${prefix}/ATLAS_AI_COMPLETE`
export const ATLAS_AI_FAILED = `${prefix}/ATLAS_AI_FAILED`
export const ATLAS_AI_CANCELLED = `${prefix}/ATLAS_AI_CANCELLED`
export const ATLAS_AI_MEMBER_JOINED = `${prefix}/ATLAS_AI_MEMBER_JOINED`
export const ATLAS_AI_MEMBER_LEFT = `${prefix}/ATLAS_AI_MEMBER_LEFT`
// Fired immediately after a user's question is persisted, before the agent
// starts. Fans out to every active group member so non-asker viewers see the
// question (and the bot's pending state) without waiting for COMPLETE.
export const ATLAS_AI_QUESTION_POSTED = `${prefix}/ATLAS_AI_QUESTION_POSTED`
// Sent only to a single newly-joining member to replay any in-progress
// streaming response buffered server-side, so they see the complete assistant
// message even if they joined mid-stream.
export const ATLAS_AI_TURN_TEXT_REPLAY = `${prefix}/ATLAS_AI_TURN_TEXT_REPLAY`
export const ATLAS_AI_TYPING_START = `${prefix}/ATLAS_AI_TYPING_START`
export const ATLAS_AI_TYPING_STOP = `${prefix}/ATLAS_AI_TYPING_STOP`
export const REDLINE_PREDICTION_SUCCESS = `${prefix}/REDLINE_PREDICTION_SUCCESS`
export const PREDICTIONS_PROCESSING = `${prefix}/PREDICTIONS_PROCESSING`
export const PREDICTIONS_SUCCESS = `${prefix}/PREDICTIONS_SUCCESS`
// A prediction run that did not finish, which would otherwise read as one with nothing to suggest.
export const PREDICTIONS_FAILURE = `${prefix}/PREDICTIONS_FAILURE`
// Sent only to the user uploading lockbox check files, about once a second.
export const CHECKS_UPLOAD_PROGRESS = `${prefix}/CHECKS_UPLOAD_PROGRESS`
// Live status of the detached subagents spawned in a chat session. Emitted on
// spawn, on each subagent turn, and on every status transition so the chat UI
// can render a per-subagent progress panel while they run in the background.
// Payload: { sessionId, targetUserId, subagents: ISubagentStatus[] }.
export const ATLAS_AI_SUBAGENT_STATUS = `${prefix}/ATLAS_AI_SUBAGENT_STATUS`
// Live state of the session's multi-step plan. Emitted on creation, on every
// step checkpoint, and on each plan status transition so the chat UI can render
// the running checklist rather than leaving the user staring at a spinner
// through a long serial chain.
// Payload: { sessionId, targetUserId, plan: IArcPlanStatus | null }.
export const ATLAS_AI_PLAN_STATUS = `${prefix}/ATLAS_AI_PLAN_STATUS`
// A new WRITE_DANGEROUS approval was queued for a chat session — pushes the
// card to every active session member instantly instead of waiting for the
// next ATLAS_AI_COMPLETE-driven refetch.
export const APPROVAL_CREATED = `${prefix}/APPROVAL_CREATED`
// An approval left the Pending state (approved+executed or rejected) — lets
// other members of a group session drop the card without a reload.
export const APPROVAL_RESOLVED = `${prefix}/APPROVAL_RESOLVED`
// One service Arc runs, as it starts and as it ends (IArcActivity), so the screen
// can show Arc working on the rows it is changing. Session-membership scoped.
export const ARC_ACTIVITY = `${prefix}/ARC_ACTIVITY`
// Arc asked to take the person to a page in Atlas: the open_page tool. Only the asker's
// own sockets, never the rest of the session. Payload: IArcNavigation plus targetUserId
export const ARC_NAVIGATE = `${prefix}/ARC_NAVIGATE`
// The report Arc revised on the asker's screen (revise_report), for the asker's own sockets.
// Payload: IArcReportRevision plus targetUserId
export const ARC_REPORT_REVISION = `${prefix}/ARC_REPORT_REVISION`
// Arc started (isArcWorking: true) or finished (false) working a note thread it
// was @-tagged in. Flips the "Arc is working…" loading indicator on the note
// live for internal viewers, without waiting for a list refetch. Payload:
// { noteId (thread root), isArcWorking }.
export const ATLAS_AI_NOTE_WORKING = `${prefix}/ATLAS_AI_NOTE_WORKING`
// Arc's live progress while working a note thread it was @-tagged in — the same
// reasoning trace shown in the Atlas chat, mirrored onto the note so internal
// viewers watch it think in place (rather than only a binary "working…"). Both
// fan out to every internal user like ATLAS_AI_NOTE_WORKING; the note (root) id
// keys them to the right thread.
//   NOTE_THINKING  payload: { noteId, thinking }        — latest status line
//   NOTE_TURN_TEXT payload: { noteId, turnIndex, text, toolsCalled, stopReason } — one reasoning step
export const ATLAS_AI_NOTE_THINKING = `${prefix}/ATLAS_AI_NOTE_THINKING`
export const ATLAS_AI_NOTE_TURN_TEXT = `${prefix}/ATLAS_AI_NOTE_TURN_TEXT`
// A bot (Arc or Dwight Bot) started or finished working a task. The task
// equivalent of ATLAS_AI_NOTE_WORKING: flips the "<name> is working…" line on the
// row and in the detail panel live, so a finished — or stopped, or failed — run
// clears the indicator without a refetch. Payload: { taskId, workingUserId,
// workingUserName?, arcStoppableByUserIds?, canStopArc? } — the ids
// (assignee + collaborators) ride along on the raising flip because stop
// eligibility is otherwise only annotated at read time, and this event fans out
// to every internal user rather than being resolved per viewer.
export const ATLAS_AI_TASK_WORKING = `${prefix}/ATLAS_AI_TASK_WORKING`

// --- Team messaging (channels / DMs) ---------------------------------------
// All CHANNEL_* events fan out to the active members of the channel named by
// `channelId` in the payload, resolved through getChannelMemberIds — the same
// membership-scoped strategy the ATLAS_AI_* events use for chat sessions.
// A message was posted, edited, or removed. DELETED carries only ids so a
// client can drop the row without leaking the removed body.
//   CREATED payload: { channelId, message: IChannelMessage }
//   UPDATED payload: { channelId, message: IChannelMessage }
//   DELETED payload: { channelId, messageId }
export const CHANNEL_MESSAGE_CREATED = `${prefix}/CHANNEL_MESSAGE_CREATED`
export const CHANNEL_MESSAGE_UPDATED = `${prefix}/CHANNEL_MESSAGE_UPDATED`
export const CHANNEL_MESSAGE_DELETED = `${prefix}/CHANNEL_MESSAGE_DELETED`
// The full reaction set for one message after an add or remove, so clients
// never have to reconcile increments.
// Payload: { channelId, messageId, reactions: IChannelMessageReactionSummary[] }
export const CHANNEL_REACTION_CHANGED = `${prefix}/CHANNEL_REACTION_CHANGED`
// Membership changes. These also invalidate the server-side member-id cache,
// so they must be emitted on every join/leave — a silent membership write
// leaves fanout stale for the cache TTL.
// Payload: IChannelMembershipEvent
export const CHANNEL_MEMBER_JOINED = `${prefix}/CHANNEL_MEMBER_JOINED`
export const CHANNEL_MEMBER_LEFT = `${prefix}/CHANNEL_MEMBER_LEFT`
// Channel metadata changed (name, topic, visibility, archived, or the `kind` a
// roster change regraded). `dmKey` is non-null only when the participant set
// itself changed, which is when the sidebar has to move the row to another
// group — see broadcastChannelUpdated.
// Payload: { channelId, channel: IChannel, dmKey: string | null }
export const CHANNEL_UPDATED = `${prefix}/CHANNEL_UPDATED`
// One user's own conversation list changed in a way only they can see —
// archiving, removing, pinning, or changing a conversation's notification
// volume. All four are per-member state, so there is nothing to fan out and
// nothing another member should learn; the acting user's other tabs and devices
// simply refetch rather than sitting on a list that disagrees until a reload.
// Payload: { targetUserId }
export const CHANNEL_LIST_CHANGED = `${prefix}/CHANNEL_LIST_CHANGED`
// One of the user's own scheduled messages was created, edited or changed status
// (sent, failed, cancelled). Only its sender ever sees a scheduled message, so this
// goes strictly to their own sockets.
// Payload: { targetUserId, scheduledMessage: IScheduledChannelMessage }
export const CHANNEL_SCHEDULED_MESSAGE_CHANGED = `${prefix}/CHANNEL_SCHEDULED_MESSAGE_CHANGED`
// Typing indicators, mirroring ATLAS_AI_TYPING_*: the client sends START/STOP
// with { channelId }, the server stamps userId from the authenticated socket
// and re-publishes to every other active member (the sender is excluded).
export const CHANNEL_TYPING_START = `${prefix}/CHANNEL_TYPING_START`
export const CHANNEL_TYPING_STOP = `${prefix}/CHANNEL_TYPING_STOP`
// A huddle in the conversation started, changed hands or ended. To the channel's members.
// Payload: { channelId, huddle: IHuddle }
export const HUDDLE_UPDATED = `${prefix}/HUDDLE_UPDATED`
// One WebRTC negotiation step between two peers of a huddle. The client sends an
// IHuddleSignal; the server stamps fromUserId and delivers it to the target peer's user only.
export const HUDDLE_SIGNAL = `${prefix}/HUDDLE_SIGNAL`
// Whether one person is in a huddle now, for the headphones beside their name. Workspace-wide
// like presence, and one message per person: it never says which huddle or with whom.
// Payload: IHuddlePresence
export const HUDDLE_PRESENCE = `${prefix}/HUDDLE_PRESENCE`
// Someone in a huddle asked these people to join. To the invitees' own sockets only.
// Payload: IHuddleInvite & { targetUserIds }
export const HUDDLE_INVITE = `${prefix}/HUDDLE_INVITE`
// Arc's host says this person just spoke to Arc: their own browser confirms it, or not.
// To the named person's sockets only. Payload: IHuddleSpeakerCheck & { targetUserId }
export const HUDDLE_SPEAKER_CHECK = `${prefix}/HUDDLE_SPEAKER_CHECK`
// The sender's own read cursor moved — lets that user's other open tabs and
// devices clear the unread badge. Strictly single-user, never fanned out.
// Payload: { channelId, targetUserId, lastReadMessageId, lastReadAt }
export const CHANNEL_READ_STATE_CHANGED = `${prefix}/CHANNEL_READ_STATE_CHANGED`
// Arc's live status line and reasoning steps while it works a channel thread it
// was @-mentioned in. The channel analogue of ATLAS_AI_NOTE_* above, with one
// deliberate difference: those fan out to every internal user because a note
// thread is visible to all of them, whereas these are membership-scoped like
// every other CHANNEL_* event — otherwise a private channel's reasoning trace
// would reach sockets that can't read the conversation it belongs to.
// Payload: { channelId, rootMessageId, thinking }
export const CHANNEL_ARC_THINKING = `${prefix}/CHANNEL_ARC_THINKING`
// Payload: { channelId, rootMessageId, turnIndex, text, toolsCalled, stopReason }
export const CHANNEL_ARC_TURN_TEXT = `${prefix}/CHANNEL_ARC_TURN_TEXT`
// Somebody pressed Stop on an Arc run in a conversation.
//
// A cancelled turn never mirrors a reply (the mirror only fires on a COMPLETED
// status), and the reply landing is what otherwise clears the working line — so
// without this event every member watching keeps a spinner that outlives the run
// it was reporting on. Membership-scoped like its two siblings above.
// Payload: { channelId, rootMessageId, sessionId }
export const CHANNEL_ARC_CANCELLED = `${prefix}/CHANNEL_ARC_CANCELLED`
// An Arc run in a conversation ended without a reply (it chose to stay quiet), so
// nothing else will clear the working line. Payload: { channelId, rootMessageId, sessionId }
export const CHANNEL_ARC_SETTLED = `${prefix}/CHANNEL_ARC_SETTLED`
// An Arc run in a conversation changed portal data, so open pages can refresh. Reaches the
// conversation's members, not just the session's. Payload: { channelId, sessionId, messageId, executedServices }
export const CHANNEL_ARC_WROTE = `${prefix}/CHANNEL_ARC_WROTE`
// A user came online or went offline. Broadcast to all internal users on
// transition only (not per heartbeat), since presence is workspace-wide rather
// than scoped to any one channel.
// Payload: { userId, isOnline, lastSeenAt }
export const PRESENCE_CHANGED = `${prefix}/PRESENCE_CHANGED`

// Someone changed their own nickname or personal status. Broadcast to all
// internal users on the same reasoning as PRESENCE_CHANGED: identity is
// workspace-wide, not scoped to a channel, and it is read well outside AIM
// (assignee lists, notes, task rows).
//
// Carries the resolved values rather than a "go refetch" nudge — the alternative
// is every connected client hitting the roster endpoint at once because one
// person set a status.
// Payload: { userId, nickname, name, aimStatus }
export const AIM_PROFILE_CHANGED = `${prefix}/AIM_PROFILE_CHANGED`

// The assignee's unread task count changed — a task they've never opened was
// created/reopened/reassigned/deleted for them, or they just opened one. Strictly
// the assignee's own sockets, mirroring CHANNEL_READ_STATE_CHANGED: this is a
// personal badge count, never fanned out to anyone else.
// Payload: { targetUserId, unreadCount }
export const TASK_UNREAD_COUNT_CHANGED = `${prefix}/TASK_UNREAD_COUNT_CHANGED`

// A project admin changed someone's role on a task project. Strictly that
// member's own sockets, so an open projects list or board regates its admin
// actions without a reload.
// Payload: { targetUserId, projectId, role }
export const TASK_PROJECT_ROLE_CHANGED = `${prefix}/TASK_PROJECT_ROLE_CHANGED`

// Live updates for an open Task Project page. The client sends SUBSCRIBE /
// UNSUBSCRIBE with { projectId } while the page is mounted; the server holds the
// subscription on that socket only, so the client re-sends it on every connect.
// CHANGED goes to subscribed sockets whose user can still see the project,
// re-checked per event so removing a member cuts their feed immediately.
// Payload (CHANGED): ITaskProjectChangedEvent
export const TASK_PROJECT_SUBSCRIBE = `${prefix}/TASK_PROJECT_SUBSCRIBE`
export const TASK_PROJECT_UNSUBSCRIBE = `${prefix}/TASK_PROJECT_UNSUBSCRIBE`
export const TASK_PROJECT_CHANGED = `${prefix}/TASK_PROJECT_CHANGED`
// Live updates for an open My Tasks page, the same way: SUBSCRIBE / UNSUBSCRIBE
// while mounted, re-sent on every connect. CHANGED goes to every subscribed TASKS
// user, since any of them can list any task; the page decides whether to refetch.
// Payload (CHANGED): IMyTasksChangedEvent
export const MY_TASKS_SUBSCRIBE = `${prefix}/MY_TASKS_SUBSCRIBE`
export const MY_TASKS_UNSUBSCRIBE = `${prefix}/MY_TASKS_UNSUBSCRIBE`
export const MY_TASKS_CHANGED = `${prefix}/MY_TASKS_CHANGED`
// Sent on every API request so a live-update event can name the tab that caused
// it, and that tab can ignore the echo of its own change.
export const CLIENT_TAB_ID_HEADER = 'x-client-tab-id'

// A Twilio number received an inbound SMS/voice callback — a portal
// verification code just landed. Broadcast to ops (the PORTFOLIO group), the
// same audience allowed to read the numbers, so an open phone-number screen
// refreshes without polling.
// Deliberately carries NO message body: the code is fetched back through
// ListMessages, which is permission-checked, rather than pushed to every
// connected ops socket regardless of what they are looking at.
// Payload: { messageId, phoneNumberId, phoneNumber, channel, receivedAt }
export const TWILIO_MESSAGE_RECEIVED = `${prefix}/TWILIO_MESSAGE_RECEIVED`

export const prepareMessage = (type: string, data: unknown) => {
  try {
    const prepared = JSON.stringify({
      type,
      data,
    })
    return prepared
  } catch (err) {
    return type
  }
}

export const formatMessage = (data: string): { type?: string; [key: string]: unknown } => {
  try {
    const parsed = JSON.parse(data) as { type?: string; [key: string]: unknown }
    return parsed
  } catch (err) {
    return { type: undefined, raw: data }
  }
}

// An Arc Live session started, reopened or ended. Strictly the host's own sockets.
//   payload: { targetUserId, session: IArcLiveSession }
export const ARC_LIVE_SESSION_UPDATED = `${prefix}/ARC_LIVE_SESSION_UPDATED`

// New or revised transcript lines on a live call. Strictly the host's own sockets.
//   payload: { targetUserId, sessionId, segments: IArcLiveSegment[] }
export const ARC_LIVE_TRANSCRIPT = `${prefix}/ARC_LIVE_TRANSCRIPT`
// One of Arc's triage checks on a live call. Strictly the host's own sockets.
//   payload: { targetUserId, sessionId, decision: IArcLiveDecision }
export const ARC_LIVE_DECISION = `${prefix}/ARC_LIVE_DECISION`
